import {
  EventLimit,
  TimeLimit,
  MaxRetryCount,
  TrackEndPoint,
  envMap,
  UserSessionEndPoint,
  COSMOS_USER_SESSION
} from "./constants";

import { getApiBaseURL } from "./helper";

/**
 * Type of tracking properties passed.
 * @typedef {Object} eventPropertiesType
 * @property {Object} event_attributes - represents attributes related to the event.
 */

/**
 * Type of restAttr passed.
 * Rest Attributes are used to pass rest attributes along with user attributes in user session token call.
 * @typedef {Object} restAttrtibutesType
 * @property {number} source - represents the source of the event (e.g. DIY, Partner, etc.)
 * @property {number} [sub_source] - represents the sub-source of the event (e.g. campaign, source, etc.)
 * @property {string} [sub_source_id] - represents the unique identifier of the sub-source.
 */

/* Global values */
let initialized = false;
let timer = null;
let eventArray = [];
let userAttributes = {};
let config = {
  eventLimit: EventLimit,
  timeLimit: TimeLimit,
  logging: false,
  retryCount: MaxRetryCount,
  debug: false,
  env: envMap.STAGING,
};
let restAttributes = {};
let API_URL = envMap.STAGING;
let userSessionToken = "";
let isSessionCallInProgress = false;
let isEventsOnHold = false;

/**
 * Start session function is used to fetch user session token.
 * This is required for tracking call. There will be no TRACKING
 * call if token is not present.
 * Case1: If token is not available and no user data is passed
 *        then it will return a token for logout users.
 * Case2: If token is available and user data is passed
 *        then it will return a logged in user token(mapped
 *        with old token internally in BE). This will be called
 *        from refreshSession method with argument true.
 * Case3: If token is expired then also it is called from refreshToken
 *        fn where we will pass old token and it will get new token.
 *        This will be called from refreshSession method with argument true.
 * @param {boolean} [isNewToken=false] - isNewToken will decide if new token
 *                                             is required and ignore local storage token.
 */
const startSession = async (isNewToken = false) => {
  if (config.debug) {
    return;
  }

  if(!userSessionToken) {
    userSessionToken = localStorage.getItem(COSMOS_USER_SESSION);
  }
  if (userSessionToken && !isNewToken) {
    if (isEventsOnHold) {
      flush();
    }
    return;
  }
  isSessionCallInProgress = true;
  let reqBody = { ...userAttributes, ...restAttributes };
  if (userSessionToken) {
    // If token is already available then pass it in the body
    // Case 2 and Case3
    // else Case 1.
    reqBody.token = userSessionToken;
  }
  try {
    logger("cosmos - Initiating session token call with req - ", reqBody, isNewToken);
    const res = await fetch(API_URL + UserSessionEndPoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reqBody),
    });
    logger("cosmos - Response after token call - ", res);
    if (res.ok) {
      const response = await res?.json();
      userSessionToken = response?.data?.token;
      if (userSessionToken) {
        localStorage.setItem(COSMOS_USER_SESSION, userSessionToken);
      }
      isSessionCallInProgress = false;
      if (isEventsOnHold) {
        flush();
      }
    }
  } catch (error) {
    isSessionCallInProgress = false;
    logger("cosmos - error while fetching session token - ", error);
  }
};

/**
 * RefreshSession is used to generate new user session token
 * in case old token is expired. API returns 401 if there is
 * any problem in Authentication and there we will call refresh
 * session which will fetch new token.
 */
const refreshSession = () => {
  if (!isSessionCallInProgress) {
    startSession(true);
  }
};

/* Function making the tracking fetch call */
const trackingCall = (eventData) => {
  return fetch(API_URL + TrackEndPoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-session-token": userSessionToken,
    },
    body: JSON.stringify(eventData),
  });
};

/**
 * This function will check if limits are reached.
 * If limits are reached then make tracking call,
 * if limits have not reached then do nothing.
 */
const checkTrackingLimits = () => {
  if (eventArray.length >= config.eventLimit) {
    flush();
  }
};

const logger = (...values) => {
  if (config.logging || config.debug) {
    console.log(...values);
  }
};

/**
 * This function is used to start time out
 * if time limit is passed in the config
 */
const startTimeoutLimit = () => {
  if (config.timeLimit && eventArray.length > 0 && !timer) {
    timer = setTimeout(() => {
      flush();
    }, config.timeLimit * 1000);
  }
};

/**
 * This function is used to stop time out
 * if time limit is passed in the config
 */
const clearTimeoutLimit = () => {
  if (config.timeLimit) {
    clearTimeout(timer);
    timer = null;
  }
};

export const user = {
  /**
   * login - function to startSession.
   * @param {object} userAttr - user attribute passed in the function
   */
  login: (userAttr={}) => {
    user.updateAttributes({...userAttr});
    logger("cosmos - setting user attributes in login", userAttributes);
    startSession(true);
  },
  /**
   * updateAttributes - Use to set/update user attributes.
   * @param {object} userAttributeObj - user attribute passed in the function
   */
  updateAttributes: (userAttributeObj) => {
    userAttributes = { ...userAttributes, ...userAttributeObj };
  },
  /**
   * logout - function to reset session and user.
   */
  logout: () => {
    logger("cosmos - logging out from cosmos");
    localStorage.removeItem(COSMOS_USER_SESSION);
    userAttributes = {};
    userSessionToken = "";
  },
};

/**
 * Setting rest attributes which will be appended in
 * user session token call
 * @param {restAttrtibutesType} restAttributeObj
 */
export const setRestAttributes = (restAttributeObj = {}) => {
  restAttributes = { ...restAttributes, ...restAttributeObj };
};

/**
 *  initialize should be called once analytic library is
 *  initialized. This is the best place to setup initial values like limits.
 *  It accepts obj which is the config.
 *  @param {Object} initConfig
 *  @property {number} [eventLimit=1] - event limit that will decide when to call tracking.
 *  @property {number} [timeLimit=0] - time limit that will decide when to call tracking.
 *  @property {boolean} [logging=false] - flag to determine if logs are enable.
 *  @property {boolean} [debug=false] - debug flag is used to disable api call and log instead.
 *  @property {string} [env=envMap.STAGING] - env is used to define environment.
 *  @property {number} [retryCount=3] - retry count if track api fails.
 *  @param {restAttrtibutesType} restAttrObj - common rest attributes use to set in session call.
 */
export const initialize = (initConfig = {}, restAttrObj={}) => {
  if (initialized) {
    logger("cosmos - cosmos is already initialized");
    return;
  }
  initialized = true;
  config = { ...config, ...initConfig };
  setRestAttributes(restAttrObj);
  logger("cosmos - values passed in initialize - ", initConfig);
  API_URL = getApiBaseURL(config.env);
  if (!config.debug) {
    window.addEventListener("visibilitychange", () => {
      logger("cosmos - inside beacon with events - ", eventArray);
      if (eventArray.length > 0 && userSessionToken) {
        let headers = {
          type: "application/json",
          "x-session-token": userSessionToken,
        };
        if (navigator && typeof navigator.sendBeacon === "function") {
          logger("cosmos - calling beacon with events", eventArray);
          clearTimeoutLimit();
          let blob = new Blob(
            [JSON.stringify({ events: eventArray })],
            headers
          );
          navigator.sendBeacon(
            API_URL + TrackEndPoint + `?x-access-token=${userSessionToken}`,
            blob
          );
        }
      }
    });
  }
  startSession();
};

export const flush = async () => {
  if (eventArray.length > 0) {
    clearTimeoutLimit();
    let retryCount = 1;
    let eventBatch = eventArray.splice(0, eventArray.length);
    logger("cosmos - flush having batch - ", eventBatch);
    let eventDataObj = {
      events: [...eventBatch],
    };
    while (retryCount <= config.retryCount) {
      try {
        if (config.debug) {
          logger("cosmos - current event in tracking", eventBatch);
          return;
        }
        if (isSessionCallInProgress) {
          eventArray.push(...eventBatch);
          isEventsOnHold = true;
          logger("cosmos - Batch return since session call is in progress");
          return;
        }
        const res = await trackingCall(eventDataObj);
        if (res.status === 401) {
          isEventsOnHold = true;
          eventArray.push(...eventBatch);
          logger("cosmos - Token issue, fetching new session token");
          refreshSession();
        } else if (res.ok) {
          const response = await res.json();
          isEventsOnHold = false;
          logger("cosmos - Flush successfull, Response ", response);
          break;
        }
      } catch (error) {
        logger("cosmos - error - ", error);
      }
      retryCount++;
    }
    if (retryCount > config.retryCount) {
      eventArray.push(...eventBatch);
      startTimeoutLimit();
    }
  } else {
    logger("cosmos - no event to track");
  }
};

/**
 * Track event is used to batch track events and
 * trigger tracking if limits are reached.
 * @param {string} name - event name passed in track event.
 * @param {eventPropertiesType} properties - event properties passed in track event.
 */
export const track = (name, properties) => {
  eventArray.push({
    event_name: name,
    ...properties,
  });
  logger("cosmos - track called, eventArray after track - ", eventArray);
  startTimeoutLimit();
  checkTrackingLimits();
};
